import React,{useEffect, useState} from 'react';
import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import upload from '@/assets/images/upload.svg';
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from 'react-router';
import TextField from '@mui/material/TextField';
import Spinner from '@/views/components/Spinner/Spinner';
import api from '@/services/api';
import { useDropzone } from "react-dropzone";
import mammoth from 'mammoth';
import ModalTips from '@/views/pages/SimpleText/modals/ModalTips.js';
import { useSelector } from "react-redux";
import { useErrorFile, useAcceptedFile } from "@/utils/utils.js";
import Alert from '@/views/components/Alert/Alert';
import { CheckPermission } from '@/utils/permissions/permissions';
import CustomSelect from '@/views/components/CustomSelect/CustomSelect';

import SimpleTextStats from '@/views/pages/SimpleText/SimpleTextStats';

import styles from '@/views/pages/SimpleText/SimpleText.module.scss';

export const SimpleText = () => {
	const { user: { role: userRole } } = useSelector((state) => state.auth);

	//const [ textConverted, setTextConverted ] = useState(false);
	const [ requestIsPending, setRequestIsPending ] = useState(false);
	const [ orginalText, setOrginalText ] = useState('');
	const [ convertedText, setConvertedText ] = useState('');
	const [ wordsNumber, setWordNumber ] = useState(0);
	const [ wordsConvertedNumber, setWordsConvertedNumber ] = useState(0);
	const [ isSubmited, setIsSubmited ] = useState(false);
	const [ myFiles, setMyFiles ] = useState([]);
	const [ error, setError ] = useState('');
	const [ copyTextClicked, setCopyTextClicked ] = useState(false);
	const [ showModalTips, setShowModalTips ] = useState(false);
	const [ listOfModels, setListOfModels ] = useState([]);
	const [ selectedModel, setSelectedModel ] = useState();
	const [ conversionError, setConversionError ] = useState();

	const [ simpleStats, setSimpleStats ] = useState();

	const navigate = useNavigate();

	const dict = useSelector(state => state.app.dict);

	const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
		accept: {
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [ ".docx" ],
		},
		multiple: false,
	});

	const { control, formState: { errors } } = useForm({
		reValidateMode: 'onChange',
		mode: 'onChange',
		defaultValues: {orginalText: '', convertedText: ''},
	});

	const getErrorFile = useErrorFile();

	const getAcceptedFile = useAcceptedFile();

	const countWords = (str) => {
		let words = str.trim().split(/\s+/);
		return words.length;
	};

	const getModels = async () => {
		try {
			const modelList = await api.get(`/ai-models`);
			let values = modelList.data.map((el) => ({ value: el.code, label: el.label }));
			setListOfModels(values);
			setSelectedModel(values[0]);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Failed to download list of models: ', err);
		}
	};

	useEffect(() => {
		getModels();
	}, []);

	const handleCopyClick = async () => {
		try {
			await navigator.clipboard.writeText(convertedText);
			setCopyTextClicked(true);
			setTimeout(() => {
				setCopyTextClicked(false);
			}, 5000);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Failed to copy text: ', err);
		}
	};

	const handleAddTextClick = async () => {
		try {
			const uploadedText = await api.post(`/ai/docx`, {text: convertedText});
			navigate(`/add-letter?fileId=${uploadedText.data.id}`);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Failed to upload text: ', err);
		}
	};

	const onDropFile = (file) => {
		setRequestIsPending(true);

		const reader = new FileReader();

		reader.onload = (event) => {
			const arrayBuffer = event.target.result;

			mammoth.extractRawText({ arrayBuffer: arrayBuffer })
				.then((result) => {
					const convertedTextt = result.value.replace(/\n{3,}/g, '\n\n');
					setWordNumber(countWords(convertedTextt));
					setOrginalText(convertedTextt);  // Set the content to state
					setRequestIsPending(false);
					setIsSubmited(true);
				})
				.catch(() => {
					setRequestIsPending(false);
				});
		};
		reader.readAsArrayBuffer(file);
	};

	const onConvert = async () => {
		setConversionError(undefined);
		setRequestIsPending(true);
		try {
			const {data: {
				simplification, 
				inputStatistics, 
				outputStatistics,
				simplificationSummary,
			}} = await api.post(`/ai/plain-text-extend`, {text: orginalText, model: selectedModel.value}, { ignoreRedirectInterceptors: true });
			setWordsConvertedNumber(countWords(simplification));
			setConvertedText(simplification);
			setSimpleStats({
				inputStatistics,
				outputStatistics,
				simplificationSummary,
			});
			//setTextConverted(true);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
			setConversionError(error.response.data.error);
		} finally {
			setRequestIsPending(false);
		}
	};

	useEffect(() => {
		if(acceptedFiles.length > 0) {
			onDropFile(acceptedFiles[0]);
			setMyFiles(getAcceptedFile(acceptedFiles));
		}
	}, [ acceptedFiles ]);

	useEffect(() => {
		if(fileRejections.length > 0) {
			const err = fileRejections.map(f => f.errors.map(e => getErrorFile(e.code)));
			setError(err[0]);
		} else {
			setError('');	
		}
	}, [ fileRejections ]);
	
	const handleChange = (e) => {
		const string = e.target.value;

		if(string === '') {
			setIsSubmited(false);
		} else {
			setIsSubmited(true);
		}
		setWordNumber(countWords(string));
		setOrginalText(string);
	};

	const handleChangeConverted = (e) => {
		const string = e.target.value;
		setWordsConvertedNumber(countWords(string));
		setConvertedText(string);
	};

	const handleReset = () => {
		//setTextConverted(false);
		setIsSubmited(false);
		setOrginalText('');
		setConvertedText('');
		setWordNumber(0);
		setWordsConvertedNumber(0);
		setMyFiles('');
		setSimpleStats(null);
	};

	const {title, description, loadingText, editorDescription1, editorDescription2, error1, warning1, warning2, buttons, tabText, tabText2, words, avoid, placeholderText, dropzone, copyText, useModel} = dict.simpletext;

	return (
		<div className={ styles['simple-text__wrapper'] }>
			<ModalTips 
				showModalTips={ showModalTips }
				setShowModalTips={ setShowModalTips } 
			/>
			{ requestIsPending && (
				<div className='letter__edit-metadata--spinner'>
					<Spinner text={ loadingText }  />
				</div>
			) }
			<Header narrow={ false } />
			<div className={ styles['simple-text__topper'] }>
				<div className={ styles['simple-text__topper__container'] }>
					<div>
						<h2 className={ styles['simple-text__topper__title'] }>{title}</h2>
						{userRole === 'reader' && <p className={ styles['simple-text__topper__description'] }>{description}</p>}
						{userRole !== 'reader' && <p className={ styles['simple-text__topper__description'] }>{editorDescription1}<br />{editorDescription2}</p>}
					</div>
					<div className={ styles['simple-text__topper__simplification'] }>
						{/*<button className="button__transparent"
							onClick={ () => handleReset() }><i className='icon-close'/>{buttons.clear}</button>*/}
						<button 
							className={ styles['simple-text__topper__simplification--button'] }
							onClick={ () => setShowModalTips(true) }
						>
							<i className='icon-circle-info' />
							{avoid}
						</button>
					</div>
				</div>
				{ !simpleStats && <Alert
					variant='info'
					alertContent={ <><strong>{warning1}</strong>{warning2}</> }
				/>}

				{ conversionError && <Alert
					variant='error'
					alertContent={ <><strong>{error1}</strong>{conversionError}</> }
				/>}
			</div>

			{ simpleStats && <SimpleTextStats 
				simpleStats={ simpleStats } 
				clearText={ handleReset }
				addText={ handleAddTextClick }
				convertedText={ convertedText }
				userRole={ userRole }
			/> }

			<div className={ styles['simple-text__converter'] }>
				<div className={ styles['simple-text__converter__box'] }>
					<div className={ styles['simple-text__converter__tabs'] }>
						<div className={ styles['simple-text__converter__top'] }>
							<div className={ styles['simple-text__converter__tab'] }>
								<span className={ styles['simple-text__converter__tab-text'] }>{tabText}</span>
							</div>
							<div className={ styles['simple-text__converter__tab-divider'] } />
							<div className={ styles['simple-text__converter__words-tabs'] }>
								<span className={ styles['simple-text__converter__words'] }>{words} {wordsNumber}</span>
							</div>
						</div>
					</div>
					<div className={ styles['simple-text__converter__area'] }>
						<>
							<Controller
								name={ 'orginalText' }
								control={ control }
								errors={ errors.name }
								render={ ({ field }) => (
									<TextField
										{ ...field }
										onChange={ (e) => handleChange(e) }
										className={ styles['simple-text__converter__textarea'] }
										multiline
										rows={ isSubmited ? 19 : 10 } // minimum rows
										variant="outlined"
										value={ orginalText }
										fullWidth
										placeholder={ placeholderText }
										style={ !isSubmited ? { height: 'calc(100% - 200px)' } : {} }
									/>
								) }
							/>
							{!isSubmited && <div>
								<div { ...getRootProps({ className: styles['simple-text__dropzone'] }) }>
									<input { ...getInputProps() } />
									<img src={ upload } />
									<p className={ styles['simple-text__dropzone-text'] }>{dropzone.info}</p>
									{myFiles && myFiles}
									{error && <p className={ styles["simple-text__error"] }>
										{error}
									</p>}
								</div>
							</div>
							}
							
						</>
					</div>
					<div className={ styles['simple-text__converter__footer'] }>
						<div className={ styles['simple-text__converter__footer__model-select'] }>
							<label>{useModel}</label>
							<CustomSelect
								options={ listOfModels } 
								value={ selectedModel }
								onChange={ (value) => { setSelectedModel(value); } }
								placeholder=""
							/> 		
						</div>	
						<button
							className={ `button__blue ${orginalText === '' ? 'disabled' : ''}` }
							onClick={ onConvert }
							disabled={ requestIsPending }
							//disabled={ orginalText === '' ? true : false }
						>
							{buttons.convert}
						</button>
					</div>
				</div>
				<div className={ styles['simple-text__converter__box'] }>
					<div className={ styles['simple-text__converter__tabs'] }>
						<div className={ styles['simple-text__converter__top'] }>
							<div className={ styles['simple-text__converter__tab'] }>
								<span className={ styles['simple-text__converter__tab-text'] }>{tabText2}</span>
							</div>
							<div className={ styles['simple-text__converter__tab-divider'] } />
							<div className={ styles['simple-text__converter__words-tabs'] }>
								<span className={ styles['simple-text__converter__words'] }>{words} {wordsConvertedNumber}</span>
							</div>
						</div>
					</div>
					<div className={ styles['simple-text__converter__area'] }>
						<Controller			
							name={ 'convertedText' }
							control={ control }
							errors={ errors.name }
							size='small'
							fullWidth
							render={ ({ field }) => (
								<TextField
									{ ...field }
									onChange={ (e) => handleChangeConverted(e) }
									className={ styles['simple-text__converter__textarea'] }
									multiline
									rows={ 19 } // minimum rows
									variant="outlined"
									value={ convertedText }
									fullWidth
								/>
							) }
						/>
					</div>
					<div className={ styles['simple-text__converter__footer'] }>	
						{copyTextClicked && <p className={ styles['simple-text__warning'] }>{copyText}</p>}
						<button
							className={ `button__white` }
							onClick={ () => handleCopyClick() }
							disabled={ !convertedText }
						>
							{buttons.copy}
						</button>
						{CheckPermission("canCreateLetter", userRole) && <button
							className={ `button__white` }
							onClick={ () => handleAddTextClick() }
							disabled={ !convertedText }
						>
							{buttons.saveText}
						</button>}
					</div>
				</div>
			</div>				
			<Footer narrow={ false } />
		</div>
	);
};

export default SimpleText;